import { Component, ElementRef, ViewChild } from '@angular/core';
import { AuthService, ConfigService, PersonService } from './services';
import { UserAttributes } from './models';
import { map } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { RolePermissionService } from './services/role-permission.service';
import { UserModalComponent } from './components/views/user-modal/user-modal.component';

interface UsdaVerificationState {
  user: UserAttributes;
  verified: boolean;
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.less'],
    standalone: false
})
export class AppComponent {
  title = 'CIAO | Check In And Out';
  underConstruction: boolean;
  // The switcher only renders when the backend exposes /auth/dev-personas,
  // which is gated by SKIP_OAUTH — so on real-OAuth environments it 404s and
  // hides itself. It used to probe everywhere, which meant every prod page
  // load fired a request at a dev-only endpoint. Now it doesn't mount on prod
  // at all; elsewhere the probe still decides. version_name carries
  // ENVIRONMENT_NAME from the deploy, so no extra config is needed.
  showDevSwitcher = !AppComponent.isProdEnvironment(
    this.configService.getConfig('version_name')
  );
  currentUser$: Observable<UserAttributes>;
  activeRouterLinks$: Observable<{ url: string; title: string }[]>;
  currentlyNavigating$: Observable<boolean>;
  // A usda.gov login that hasn't been verified has its roles and permissions
  // significantly reduced, so the header reports verification state rather
  // than leaving it buried in the profile page. Null means "say nothing".
  usdaVerification$: Observable<UsdaVerificationState | null>;
  isResendingVerification = false;

  @ViewChild('menubtn') menubtn: ElementRef<HTMLElement>;
  @ViewChild('links') links: ElementRef<HTMLElement>;
  @ViewChild('userModal') userModal: UserModalComponent;

  constructor(
    public authService: AuthService,
    private configService: ConfigService,
    private readonly rolePermissionService: RolePermissionService,
    private readonly personService: PersonService,
    private readonly toastrService: ToastrService
  ) {}

  ngOnInit() {
    this.currentUser$ = this.authService.currentUser$;
    this.usdaVerification$ = this.currentUser$.pipe(
      map((user) => this.usdaVerificationState(user))
    );
    this.activeRouterLinks$ = combineLatest([
      this.authService.currentUser$,
      this.rolePermissionService.isAdmin$,
      this.rolePermissionService.isSystemAdmin$,
    ]).pipe(
      map(([user, isAdmin, isSystemAdmin]) => {
        if (!user) {
          return 'noUser';
        } else if (isSystemAdmin) {
          return 'systemAdminUser';
        } else if (isAdmin) {
          return 'adminUser';
        } else {
          return 'basicUser';
        }
      }),
      map((listName) => allRouterLinks[listName] || allRouterLinks.error)
    );
    try {
      this.underConstruction = this.configService.getConfig(
        'show_under_construction'
      );
    } catch (err) {
      console.error(err);
      this.underConstruction = true;
    }
  }

  ngAfterViewInit() {
    this.menubtn.nativeElement.addEventListener('click', () => {
      this.menubtn.nativeElement.classList.toggle('active');
      this.links.nativeElement.classList.toggle('active');
    });
  }
  removeActive() {
    this.menubtn.nativeElement.classList.remove('active');
    this.links.nativeElement.classList.remove('active');
  }
  openUserModal() {
    this.userModal?.openModal();
  }

  /** "prod", "production", "prod-43" — anything Jenkins tags as production. */
  static isProdEnvironment(versionName: unknown): boolean {
    return (
      typeof versionName === 'string' &&
      versionName.trim().toLowerCase().startsWith('prod')
    );
  }

  /**
   * Verification state of the address the user signed in with, or null when
   * the header should stay quiet -- either it isn't a usda.gov login, or
   * `emailVerified` is absent from the payload. It's a NOT NULL column, so an
   * absent value means "unknown", and neither claiming verified nor warning
   * unverified would be honest.
   */
  private usdaVerificationState(
    user: UserAttributes | null
  ): UsdaVerificationState | null {
    if (user?.email?.toLowerCase().endsWith('@usda.gov') !== true) return null;
    if (user.emailVerified === true) return { user, verified: true };
    if (user.emailVerified === false) return { user, verified: false };
    return null;
  }

  resendUsdaVerification(user: UserAttributes) {
    if (this.isResendingVerification) return;

    this.isResendingVerification = true;
    this.personService
      .resendEmailVerification(user.personId, user.email)
      .subscribe({
        next: () => {
          this.toastrService.success(
            'Verification email sent. Check your inbox.',
            'Success'
          );
          this.isResendingVerification = false;
        },
        error: (err) => {
          this.toastrService.error(
            err?.error?.error || 'Failed to resend verification email.',
            'Error'
          );
          this.isResendingVerification = false;
        },
      });
  }
}

const allRouterLinks = {
  error: [],
  noUser: [],
  basicUser: [
    //{ title: 'Home', url: '/home' },
    { title: 'Trips', url: '/search' },
    { title: 'Teams', url: '/teams' },
    { title: 'Help', url: '/help' },
  ],
  adminUser: [
    //{ title: 'Home', url: '/home' },
    { title: 'Trips', url: '/search' },
    { title: 'Teams', url: '/teams' },
    { title: 'Users', url: '/users' },
    { title: 'Help', url: '/help' },
  ],
  systemAdminUser: [
    //{ title: 'Home', url: '/home' },
    { title: 'Trips', url: '/search' },
    { title: 'Teams', url: '/teams' },
    { title: 'Users', url: '/users' },
    { title: 'Help', url: '/help' },
    // Change back to route appropriately after #1710
    { title: 'System Admin', url: '/system-admin/announcements' },
  ],
};
